/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import arrowExpand from '../../../../../../assets/student-training/component/arrow-icon2.png';
import arrowCollapse from '../../../../../../assets/student-training/component/arrow-icon1.png';
import arrowGif from '../../../../../../assets/2025/arrow.gif';
import './index.less';
import './index.en.less';

export interface IFlipCardItem {
  icon?: React.ReactNode;
  title: string;
  illustration?: React.ReactNode;
  content: string;
}

export interface IFlipCardGridProps {
  mainTitle?: string;
  subTitle?: string;
  description?: string;
  items: IFlipCardItem[];
  defaultActiveIndex?: number;
  id?: string;
  onComplete?: () => void;
  showGuideArrow?: boolean;
}

const FlipCardGrid: React.FunctionComponent<IFlipCardGridProps> = ({
  mainTitle,
  subTitle,
  description,
  items,
  defaultActiveIndex = -1,
  id,
  onComplete,
  showGuideArrow = true,
}) => {
  const [activeIndices, setActiveIndices] = useState<Set<number>>(
    () => (showGuideArrow ? new Set() : new Set(items.map((_, index) => index)))
  );
  const [completedIndices, setCompletedIndices] = useState<Set<number>>(new Set());
  const hasCompletedRef = useRef(false);

  useEffect(() => {
    setCompletedIndices(new Set());
    setActiveIndices(showGuideArrow ? new Set() : new Set(items.map((_, index) => index)));
    hasCompletedRef.current = false;
  }, [items.length]);

  useEffect(() => {
    if (
      items.length > 0 &&
      completedIndices.size === items.length &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      onComplete?.();
    }
  }, [completedIndices, items, onComplete]);

  const handleCardClick = (index: number) => {
    if (showGuideArrow) {
      const isCompleted = completedIndices.has(index);
      const isNextTarget = index === completedIndices.size;
      if (!isCompleted && !isNextTarget) return;
      if (isNextTarget) {
        const newCompleted = new Set(completedIndices);
        newCompleted.add(index);
        setCompletedIndices(newCompleted);
        setActiveIndices((prev) => {
          const next = new Set(prev);
          next.add(index);
          return next;
        });
      } else {
        setActiveIndices((prev) => {
          const next = new Set(prev);
          if (next.has(index)) next.delete(index);
          else next.add(index);
          return next;
        });
      }
      return;
    }
    setActiveIndices((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
    if (onComplete) {
      onComplete();
    }
  };

  const arrowTargetIndex = showGuideArrow ? completedIndices.size : -1;

  return (
    <div className="flip-card-grid" id={id}>
      <div className="fcg-header">
        {mainTitle && <h2 className="fcg-main-title">{mainTitle}</h2>}
        {subTitle && <h3 className="fcg-sub-title">{subTitle}</h3>}
        {description && <p className="fcg-description">{description}</p>}
      </div>
      <div className="fcg-grid">
        {items.map((item, index) => {
          const isActive = activeIndices.has(index);
          const showArrow = showGuideArrow && index === arrowTargetIndex;
          return (
            <div
              key={index}
              className={`fcg-card ${isActive ? 'is-active' : ''}`}
            >
              <button
                className="fcg-card-toggle"
                onClick={() => handleCardClick(index)}
              >
                <img src={isActive ? arrowCollapse : arrowExpand} alt="" />
                {showArrow && (
                  <img src={arrowGif} alt="" className="fcg-guide-arrow" />
                )}
              </button>
              <div className="fcg-card-icon">{item.icon}</div>
              <h4 className="fcg-card-title">{item.title}</h4>
              <div className="fcg-card-body">
                {isActive ? (
                  <div className="fcg-card-content">
                    <p>{item.content}</p>
                  </div>
                ) : (
                  <div className="fcg-card-illustration">
                    {item.illustration}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FlipCardGrid;
